/**
 * Security validation utilities for safe data handling
 */

import { UserProfile, UserTaskState } from '../types';

/**
 * Safe JSON parse with error handling
 * Returns null instead of throwing on invalid JSON
 */
export function safeJsonParse<T>(json: string | null): T | null {
  if (!json) return null;

  try {
    return JSON.parse(json) as T;
  } catch (e) {
    console.warn('Failed to parse JSON:', e);
    return null;
  }
}

/**
 * Validates and sanitizes a user profile object
 * Returns null if the profile is invalid or potentially malicious
 */
export function validateUserProfile(data: unknown): UserProfile | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }

  const profile = data as Record<string, unknown>;

  // Validate required fields exist and are correct types
  if (typeof profile.sourceCountry !== 'string' || !profile.sourceCountry) {
    return null;
  }

  if (typeof profile.familyType !== 'string' || !profile.familyType) {
    return null;
  }

  if (!Array.isArray(profile.childrenAges)) {
    return null;
  }

  if (typeof profile.workLocation !== 'string' || !profile.workLocation) {
    return null;
  }

  if (typeof profile.timeline !== 'string' || !profile.timeline) {
    return null;
  }

  if (typeof profile.budget !== 'string' || !profile.budget) {
    return null;
  }

  // Validate allowed values (whitelist approach)
  const allowedCountries = ['US', 'UK', 'Germany', 'Other'];
  const allowedFamilyTypes = ['single', 'couple_no_kids', 'family_with_kids'];
  const allowedWorkLocations = ['hinjewadi', 'kharadi', 'magarpatta', 'wfh', 'unsure'];
  const allowedTimelines = ['less_than_6_months', '6_to_12_months', '1_to_2_years', 'exploring'];
  const allowedBudgets = ['rent_30_50k', 'rent_50_80k', 'buy_1_2_cr', 'buy_2_4_cr', 'buy_4_plus_cr'];
  const allowedChildrenAges = ['preschool', 'elementary', 'middle_school', 'high_school'];

  if (!allowedCountries.includes(profile.sourceCountry)) {
    return null;
  }

  if (!allowedFamilyTypes.includes(profile.familyType)) {
    return null;
  }

  if (!allowedWorkLocations.includes(profile.workLocation)) {
    return null;
  }

  if (!allowedTimelines.includes(profile.timeline)) {
    return null;
  }

  if (!allowedBudgets.includes(profile.budget)) {
    return null;
  }

  // Validate children ages array
  const sanitizedChildrenAges = (profile.childrenAges as unknown[])
    .filter((age): age is string =>
      typeof age === 'string' && allowedChildrenAges.includes(age)
    );

  // Return sanitized profile
  return {
    sourceCountry: profile.sourceCountry,
    familyType: profile.familyType,
    childrenAges: sanitizedChildrenAges,
    workLocation: profile.workLocation,
    timeline: profile.timeline,
    budget: profile.budget,
    createdAt: typeof profile.createdAt === 'string' ? profile.createdAt : undefined,
    isPersonalized: profile.isPersonalized === true ? true : undefined
  };
}

/**
 * Validates and sanitizes user task state array
 * Filters out invalid entries
 */
export function validateTaskStates(data: unknown): UserTaskState[] {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .filter((item): item is Record<string, unknown> =>
      item && typeof item === 'object' && !Array.isArray(item)
    )
    .filter(item =>
      typeof item.id === 'string' &&
      item.id.length > 0 &&
      item.id.length < 100 && // Reasonable ID length
      !item.id.includes('__proto__') && // Block prototype pollution attempts
      !item.id.includes('constructor')
    )
    .map(item => ({
      id: item.id as string,
      completed: Boolean(item.completed),
      included: item.included !== false // Default to true
    }));
}

/**
 * Sanitizes a string to prevent XSS when used in certain contexts
 * Note: React already escapes content in JSX, but this is extra protection
 * for any edge cases where raw HTML might be used
 */
export function sanitizeString(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * Validates that a URL is safe (not javascript:, data:, etc.)
 */
export function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}
